import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface SelfEvalGradingConfig {
  number: number; // Number of graders to use (1-10)
  selfevalGrader1?: string;
  selfevalGrader2?: string;
  selfevalGrader3?: string;
  selfevalGrader4?: string;
  selfevalGrader5?: string;
  selfevalGrader6?: string;
  selfevalGrader7?: string;
  selfevalGrader8?: string;
  selfevalGrader9?: string;
  selfevalGrader10?: string;
}

export class SelfEvalConfigService {
  private static configCache: SelfEvalGradingConfig | null = null;
  private static lastFetch: number = 0;
  private static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get the current self-eval grading configuration
   * @returns The configuration object with grader models
   */
  static async getConfig(): Promise<SelfEvalGradingConfig | null> {
    try {
      // Return cached config if still valid
      const now = Date.now();
      if (this.configCache && (now - this.lastFetch) < this.CACHE_DURATION) {
        console.log('🔍 Using cached self-eval config');
        return this.configCache;
      }

      console.log('🔍 Fetching self-eval grading config from Firestore...');

      const configRef = doc(db, 'system', 'selfevalGradingModelTop');
      const configDoc = await getDoc(configRef);

      if (!configDoc.exists()) {
        console.warn('⚠️ No self-eval config found');
        return null;
      }

      const config = configDoc.data() as SelfEvalGradingConfig;

      // Cache the config
      this.configCache = config;
      this.lastFetch = now;

      console.log('✅ Self-eval config loaded:', {
        number: config.number,
        graders: this.getGraderList(config)
      });

      return config;
    } catch (error) {
      console.error('❌ Error fetching self-eval config:', error);
      return null;
    }
  }

  /**
   * Get the list of active grader models from the config
   * @param config - The configuration object
   * @returns Array of grader model IDs (non-empty values only)
   */
  static getGraderList(config: SelfEvalGradingConfig): string[] {
    const graders: string[] = [];
    
    for (let i = 1; i <= config.number && i <= 10; i++) {
      const graderKey = `selfevalGrader${i}` as keyof SelfEvalGradingConfig;
      const graderModel = config[graderKey];
      
      if (graderModel && typeof graderModel === 'string' && graderModel.trim() !== '') {
        graders.push(graderModel);
      }
    }

    return graders;
  }

  /**
   * Clear the cached configuration (useful for testing or forcing refresh)
   */
  static clearCache(): void {
    this.configCache = null;
    this.lastFetch = 0;
    console.log('🗑️ Self-eval config cache cleared');
  }
}

